import { useEffect, useRef, useState } from "react";
import { limit, orderBy, serverTimestamp } from "firebase/firestore";
import { useFirebase } from "../../wrappers/firebase";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
import getMillis from "../../helpers/getMillis";

/**
 * Notifications live under: profiles/{uid}/notifications
 * Fields: body, createdOn, isRead, readOn, customPath, points
 * Notifications are not created client-side; they can only be updated (e.g., mark as read).
 * No caching via react-query – keep ephemeral state only.
 */
function useNotifications() {
  const { subscribe, updateDocument } = useFirebase();
  const { uid } = useUserStore((state) => state.user, shallow) || {};
  const setUser = useUserStore((state) => state.setUser);

  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!uid) return;

    const unsubscribe = subscribe({
      path: `profiles/${uid}/notifications`,
      observerOnlyChanges: true,
      filters: [orderBy("createdOn", "desc"), limit(10)],
      callback: (data) => {
        setNotifications((prev) => {
          const map = new Map();
          // Add previous notifications first
          prev.forEach((n) => map.set(n.id, n));
          // Overwrite/add with new data
          data?.filter((dataObj) => !!dataObj).forEach((n) => map.set(n.id, n));
          // Sort by createdOn descending before setting
          return Array.from(map.values()).sort((a, b) => {
            // Handle null/undefined timestamps gracefully
            const aTime = getMillis(a.createdOn);
            const bTime = getMillis(b.createdOn);
            return bTime - aTime;
          });
        });
      },
    });

    // Subscribe to profile points
    const profilePath = `profiles/${uid}`;
    const profileUnsubscribe = subscribe({
      path: profilePath,
      observerOnlyChanges: true,
      callback: (doc) => {
        const nextPoints = typeof doc?.points === "number" ? doc.points : 0;
        setUser({ points: nextPoints });
      },
    });

    return () => {
      unsubscribe();
      profileUnsubscribe();
    };
  }, [uid, subscribe]);

  const updateNotification = async ({ notificationId, data }) => {
    if (!uid || !notificationId) return null;
    const path = `profiles/${uid}/notifications/${notificationId}`;
    return updateDocument({ path, data });
  };

  const markAsRead = async (notificationId) => {
    return updateNotification({
      notificationId,
      data: { isRead: true, readOn: serverTimestamp() },
    });
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter((n) => !n.isRead);
    await Promise.all(unreadNotifications.map((n) => markAsRead(n.id)));
  };

  const getUnreadCount = () => {
    return notifications.filter((n) => !n.isRead).length;
  };

  return {
    uid,
    notifications,
    updateNotification,
    markAsRead,
    markAllAsRead,
    getUnreadCount,
  };
}

export default useNotifications;
